import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useApiClient } from "../../contexts/ApiClientContext";
import { useAuth } from "../../contexts/AuthContext";
import { useAlerts } from "../../contexts/AlertsContext";
import APIEndPoints from "../../utils/APIEndPoints";
import AlertMessageType from "../common/alert_message/AlertMessageType";
import SETTINGS from "../../globals";
import AppSections from "../../utils/AppSections";
import { IApiClientError } from "../../shared/ApiClient";
import APIError from "../../utils/APIError";
import Button from "../common/button/Button";
import ButtonAction from "../common/button/ButtonAction";
import DialogWindowType from "../common/dialog_window/DialogWindowType";
import Loading from "../common/Loading";
import FormContainer from "../common/FormContainer";
import { FaInfoCircle } from "react-icons/fa";
import FormInputText from "../common/FormInputText";
import FormTextArea from "../common/FormTextArea";
import FormInputImage from "../common/FormInputImage";
import FormDropDown from "../common/FormDropDown";
import UploadManager from "../../shared/UploadManager";
import UploadFileType from "../../model/enums/UploadFileType";
import ImgUtil from "../../utils/ImgUtil";
import _3DInstance from "../../model/_3DInstance";
import _3DModelDetailLevel from "../../model/enums/_3DModelDetailLevel";
import _3DModelLicenseType from "../../model/enums/_3DModelLicenseType";
import FormInputFile from "../common/FormInputFile";
import AppSubSections from "../../utils/AppSubSections";
import FormDownloadFile from "../common/FormDownloadFile";
import UploadManagerProgressReport from "../../model/UploadManagerProgressReport";
import Texture from "../../model/Texture";
import TextureType from "../../model/enums/TextureType";
import PagedData from "../../model/PagedData";
import ButtonType from "../common/button/ButtonType";
import UploadFileStatus from "../../model/enums/UploadFileStatus";
import { EXPLANATION_HEADER } from "../common/dialog_window/LicenseDialog";
import TextureFile from "../../model/TextureFile";
import TextureFileType from "../../model/enums/TextureFileType";
import FormUploadTextureFile from "../common/FormUploadTextureFile";
import ExpectedFileForUpload from "../../model/ExpectedFileForUpload";

interface I3dInstanceFormErrors {
  name: string;
  description: string;
  nameTexture: string;
  descriptionTexture: string;
  detail_level: string;
  license_type: string;
  binary_file: string;
  id_gltf_file: string;
  type: string;
  gltf_images: string;
}

const defaultFormErrors: I3dInstanceFormErrors = {
  name: "",
  description: "",
  nameTexture: "",
  descriptionTexture: "",
  detail_level: "",
  license_type: "",
  binary_file: "",
  id_gltf_file: "",
  type: "",
  gltf_images: "",
};

function _3dInstanceAddEdit() {
  // read the IDs from the url
  const { e3did, id } = useParams();

  // manage loading animation
  const [loading, setLoading] = useState(true);
  // manage uploading & creating animations
  const [uploading, setUploading] = useState(false);
  // manage upload progress display
  const [uploadProgress, setUploadProgress] = useState<
    UploadManagerProgressReport | undefined
  >(undefined);
  // set the state to track any error messages received from the server
  const [errorMessage, setErrorMessage] = useState("");
  // set state for form info
  const [formData, setFormData] = useState<_3DInstance>(
    _3DInstance.EmptyInstance(),
  );
  // add one more form info for default texture
  const [formDataTexture, setFormDataTexture] = useState<Texture>(
    Texture.EmptyInstance(),
  );
  // set state for form errors
  const [formErrors, setFormErrors] =
    useState<I3dInstanceFormErrors>(defaultFormErrors);
  // store image files required by GLTF
  const [gltfImages, setGltfImages] = useState<ExpectedFileForUpload[]>([]);
  //
  const [textureFilesDetails, setTextureFilesDetails] = useState<
    Map<string, TextureFile>
  >(new Map());

  // reference to the ApiClient instance
  const apiClient = useApiClient();
  // get a reference to navigation component
  const navigate = useNavigate();
  // reference to the AuthManager
  const authManager = useAuth();
  // reference to the alerts system
  const alertsManager = useAlerts();
  // instance of UploadManager
  const updateUploadProgress = (info: UploadManagerProgressReport) => {
    setUploadProgress(info);
  };
  const [uploadManager] = useState(
    new UploadManager({
      apiClient: apiClient,
      progressCallback: updateUploadProgress,
    }),
  );
  // instance of upload manager for default texture files
  const [uploadManagerTexture] = useState(
    new UploadManager({
      apiClient: apiClient,
      progressCallback: updateUploadProgress,
    }),
  );

  // user agreement to terms and conditions
  const [agreeTerms, setAgreeTerms] = useState(false);

  // load record data from the server
  useEffect(() => {
    const getAllData = async () => {
      try {
        let _3dInstance: _3DInstance | undefined = undefined;
        if (id) {
          _3dInstance = await apiClient?.get<_3DInstance>({
            url: APIEndPoints._3DINSTANCES.ONE(parseInt(id)),
          });

          const allTextures = await apiClient?.get<PagedData<Texture[]>>({
            url: APIEndPoints.TEXTURES.ALL(parseInt(id)),
          });
          // detect the default texture
          const auxTextures = allTextures.items.filter(
            (item) => item.gltf_name,
          );
          // should be one and only one
          if (auxTextures.length < 1) {
            setErrorMessage(
              "This scan has no default texture. Delete this record and repeat the add process.",
            );
          } else {
            setFormDataTexture(auxTextures[0]);
          }
        }

        // unify initial model with data from all sources
        const currentData = _3dInstance
          ? _3dInstance
          : _3DInstance.EmptyInstance();
        if (e3did) {
          currentData.id_e3dmodel = parseInt(e3did);
        }

        // set into the state all the information
        setFormData(currentData);
        setFormErrors(defaultFormErrors);
      } catch (error: any) {
        if (!error.processed) {
          setErrorMessage("Something went wrong. Try again later!");
        }
      }
      setLoading(false);
    };
    getAllData();
  }, [apiClient, id, e3did]);

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    // validate errors from the form
    const errors = validateSiteForm(
      formData,
      formDataTexture,
      uploadManagerTexture.getFiles("textures").map((item) => item.file.name),
      gltfImages,
      textureFilesDetails,
    );

    setFormErrors(errors);
    setErrorMessage(
      isFormInvalid(errors)
        ? "Some of the data input is invalid. Please check and try again."
        : "",
    );

    if (isFormInvalid(errors)) {
      return;
    }

    var iconServerId: number | undefined = id ? formData.icon : undefined;
    var binaryServerId: number | undefined = formData.id_file;
    var uploadErrors = "";

    // we upload only if it is a CREATE or if is EDIT & we have some files (e.g. icon)
    if (!id || uploadManager.hasAnyFilesPending()) {
      try {
        setUploading(true);

        // upload the files
        const uploadResult = await uploadManager.uploadFiles();

        if (uploadResult instanceof Array) {
          uploadResult.forEach((result) => {
            if (result.status !== UploadFileStatus.COMPLETE) {
              uploadErrors += " ERROR uploading file: " + result.fileName;
            } else if (result.identifier === "icon") {
              iconServerId = result.serverId;
            } else if (result.identifier === "binary_file") {
              binaryServerId = result.serverId;
            }
          });
        }
      } catch (exception) {
        uploadErrors += " " + exception;
        console.log(exception);
      }

      if (uploadErrors !== "") {
        alertsManager.showAlertMessage({
          type: AlertMessageType.ERROR,
          message: uploadErrors,
        });
        setUploading(false);
        return;
      }
    }

    var body = new _3DInstance(
      authManager.getUserID()!!,
      formData.id_e3dmodel,
      binaryServerId,
      iconServerId,
      formData.name,
      formData.description,
      formData.format,
      formData.detail_level,
      formData.sensor_configuration,
      formData.license_type,
    );

    // check if we have EDIT or CREATE
    if (id) {
      body.id = parseInt(id);
      apiClient
        ?.put({
          url: APIEndPoints._3DINSTANCES.ONE(parseInt(id)),
          body: body,
        })
        .then((response) => {
          setFormErrors(defaultFormErrors); // Reset errors
          alertsManager.showAlertMessage({
            type: AlertMessageType.SUCCESS,
            message: "3D scan updated successfully.",
          });
          navigate(
            SETTINGS.get_path({
              section: AppSections._3DINSTANCES,
              id: parseInt(id),
              subsection: AppSubSections.VIEW,
            }),
          );
        })
        .catch((error: IApiClientError) => {
          if (!error.processed) {
            processAPIErrors(error);
          }
          setUploading(false);
        });
    } else {
      apiClient
        ?.post<_3DInstance>({
          url: APIEndPoints._3DINSTANCES.NEW(body.id_e3dmodel),
          body: body,
        })
        .then((response) => {
          setFormErrors(defaultFormErrors);
          alertsManager.showAlertMessage({
            type: AlertMessageType.SUCCESS,
            message:
              "3D scan created successfully. Creating default texture...",
          });
          handleSubmitTexture(response.id!);
        })
        .catch((error: IApiClientError) => {
          if (!error.processed) {
            processAPIErrors(error);
          }
          setUploading(false);
        });
    }
  };

  const handleSubmitTexture = async (id_3Dinstance: number) => {
    // check if the instance has been created successfully
    if (!id_3Dinstance) {
      alertsManager.showAlertMessage({
        type: AlertMessageType.SUCCESS,
        message:
          "Error while adding the 3D scan. Record is invalid. Please delete it and try again.",
      });
    }

    // create the default texture
    var iconServerId: number | undefined = id ? formData.icon : undefined;
    var gltfServerId: number | undefined = formDataTexture.id_gltf_file;
    var gltfTextureFilesServerIds: TextureFile[] = [];
    try {
      setUploading(true);
      // upload the files
      const uploadResult = await uploadManagerTexture.uploadFiles();

      if (uploadResult instanceof Array) {
        uploadResult.forEach((result) => {
          if (result.identifier === "icon") {
            iconServerId = result.serverId;
          } else if (result.identifier === "gltf_file" && result.serverId) {
            gltfServerId = result.serverId;
          } else if (result.identifier === "textures" && result.serverId) {
            gltfTextureFilesServerIds.push(
              new TextureFile(
                result.serverId,
                textureFilesDetails.get(result.fileName)?.texture_type ??
                  TextureFileType.OTHER,
              ),
            );
          }
        });
      }
    } catch (exception) {
      console.log(exception);
    }

    var body = new Texture(
      authManager.getUserID()!!,
      id_3Dinstance,
      gltfServerId,
      iconServerId,
      formDataTexture.name,
      formDataTexture.description,
      formData.sensor_configuration,
      formDataTexture.type,
      formData.license_type,
      gltfTextureFilesServerIds ? gltfTextureFilesServerIds : undefined,
      formDataTexture.config,
    );

    // here we have only CREATE
    apiClient
      ?.post<Texture>({
        url: APIEndPoints.TEXTURES.NEW(body.id_3Dinstance),
        body: body,
      })
      .then((response) => {
        setFormErrors(defaultFormErrors);
        alertsManager.showAlertMessage({
          type: AlertMessageType.SUCCESS,
          message: "Default texture created successfully.",
        });
        navigate(
          SETTINGS.get_path({
            section: AppSections._3DINSTANCES,
            id: id_3Dinstance,
            subsection: AppSubSections.VIEW,
          }),
        );
      })
      .catch((error: IApiClientError) => {
        if (!error.processed) {
          processAPIErrors(error);
        }
        setUploading(false);
      });
  };

  const handleDelete = () => {
    apiClient
      ?.delete({
        url: APIEndPoints._3DINSTANCES.ONE(parseInt(id!)),
      })
      .then((response) => {
        alertsManager.showAlertMessage({
          type: AlertMessageType.SUCCESS,
          message: "3D scan deleted successfully",
        });
        navigate(
          SETTINGS.go_back({
            section: AppSections.E3DS,
            id: formData.id_e3dmodel,
            subsection: AppSubSections.VIEW,
          }),
        );
      })
      .catch((error: IApiClientError) => {
        if (!error.processed) {
          alertsManager.showAlertMessage({
            type: AlertMessageType.ERROR,
            message: "Unable to delete 3D scan",
          });
        }
      });
  };

  const formInputChange = (inputID: string, newValue: string) => {
    const formDataCopy = JSON.parse(JSON.stringify(formData));
    formDataCopy[inputID] = newValue;
    setFormData(formDataCopy as _3DInstance);
  };

  const formInputChangeTexture = (inputID: string, newValue: string) => {
    const formDataCopy = JSON.parse(JSON.stringify(formDataTexture));
    formDataCopy[inputID] = newValue;
    setFormDataTexture(formDataCopy as Texture);
  };

  const formIconChange = (
    id: string,
    value: string | undefined,
    files: FileList | null,
  ) => {
    uploadManager.replaceFiles(id, UploadFileType.ICON, files);
    const formDataCopy = JSON.parse(JSON.stringify(formData));
    formDataCopy["icon_path"] = value;
    formDataCopy["icon"] = -1;
    setFormData(formDataCopy as _3DInstance);

    // update the icon information for the texture also
    uploadManagerTexture.replaceFiles(id, UploadFileType.ICON, files);
    const formDataCopyT = JSON.parse(JSON.stringify(formDataTexture));
    formDataCopyT["icon_path"] = value;
    formDataCopyT["icon"] = -1;
    setFormDataTexture(formDataCopyT as Texture);
  };

  const formFileChange = (
    fieldId: string,
    keyForData: string,
    keyForDisplay: string,
    files: FileList | null,
  ) => {
    uploadManager.replaceFiles(fieldId, UploadFileType.E3D_INSTANCE, files);
    // process the display, based on the name of the files
    let display = "";
    if (files) {
      for (let i = 0; i < files.length; i++) {
        display += display === "" ? files[i].name : ", " + files[i].name;
      }
    }
    const formDataCopy = JSON.parse(JSON.stringify(formData));
    formDataCopy[keyForDisplay] = display === "" ? undefined : display;
    formDataCopy[keyForData] = -1;
    setFormData(formDataCopy as _3DInstance);
  };

  const formFileChangeTexture = async (
    fieldId: string,
    keyForData: string,
    keyForDisplay: string,
    files: FileList | null,
  ) => {
    let uploadFileType: UploadFileType | undefined = undefined;
    let display = "";

    if (files && fieldId === "gltf_file") {
      uploadFileType = UploadFileType.TEXTURE_LAYER;
      uploadManagerTexture.replaceFiles(fieldId, uploadFileType, files);
      display = files[0].name;
    } else if (files && fieldId === "textures") {
      uploadFileType = UploadFileType.TEXTURE;
      // clear files for upload
      uploadManagerTexture.removeFiles(fieldId);

      var allFilesSelected: boolean = false;
      // if we have texture images uploaded, we need to make sure that the names are unique!
      const allNamesLowercase: string[] = [];
      // track the files selected for upload that are not in GLTF
      const extraFilesSelected: string[] = [];
      // reset information about meaning details => texture files can be changed only all at once
      var auxTextureFileDetails = new Map<string, TextureFile>();

      // make a copy of the list of expected files
      const expectedFilesCopy = JSON.parse(
        JSON.stringify(gltfImages),
      ) as ExpectedFileForUpload[];
      // reset all to not_selected
      expectedFilesCopy.forEach((item) => (item.already_selected = false));

      // process the display, based on the name of the files
      for (let i = 0; i < files.length; i++) {
        //display += display === "" ? files[i].name : ", " + files[i].name;
        allNamesLowercase.push(files[i].name.toLocaleLowerCase());
        // update the expected images list
        const auxExpectedRef = expectedFilesCopy.find(
          (item) =>
            item.name.toLocaleLowerCase() === files[i].name.toLocaleLowerCase(),
        );
        if (auxExpectedRef) auxExpectedRef.already_selected = true;
        else extraFilesSelected.push(files[i].name);
      }
      display =
        "Click here to select a new set of files. Current selection status:";
      setGltfImages(expectedFilesCopy);

      const errorsCopy = JSON.parse(
        JSON.stringify(formErrors),
      ) as I3dInstanceFormErrors;
      errorsCopy.gltf_images = "";

      // we need to make sure that the names are unique!
      if (new Set(allNamesLowercase).size < allNamesLowercase.length) {
        errorsCopy.gltf_images =
          "Duplicate file names are not accepted. Please make sure all files have unique names.";
        setFormErrors(errorsCopy);
        setTextureFilesDetails(auxTextureFileDetails);
        return;
      }

      // we also need to check if all the files have been selected
      allFilesSelected =
        expectedFilesCopy.filter((item) => item.already_selected === false)
          .length == 0;

      if (!allFilesSelected) {
        errorsCopy.gltf_images =
          "There are missing files from your selection. Please make the selection again.";
        setFormErrors(errorsCopy);
        setTextureFilesDetails(auxTextureFileDetails);
        return;
      }

      if (extraFilesSelected.length > 0) {
        errorsCopy.gltf_images =
          "You have selected more files than required. Please redo the selection and remove the files: " +
          extraFilesSelected.join(", ");
        setFormErrors(errorsCopy);
        setTextureFilesDetails(auxTextureFileDetails);
        return;
      }

      // queue files for upload
      uploadManagerTexture.replaceFiles(fieldId, uploadFileType, files);

      // process files meaning
      for (let i = 0; i < files.length; i++) {
        var auxMeaning = TextureFileType.NOT_SET;
        var auxFileName = files[i].name;
        // try to infer the meaning from the file name
        if (auxFileName.indexOf("norm") > -1) {
          auxMeaning = TextureFileType.NORMAL_MAP;
        } else if (auxFileName.indexOf("occ") > -1) {
          auxMeaning = TextureFileType.AMBIENT_OCCLUSION;
        } else if (auxFileName.indexOf("roug") > -1) {
          auxMeaning = TextureFileType.METALIC_ROUGHNESS;
        } else if (
          auxFileName.indexOf("alb") > -1 ||
          auxFileName.indexOf("diff") > -1
        ) {
          auxMeaning = TextureFileType.ALBEDO_DIFFUSE;
        }
        auxTextureFileDetails.set(
          auxFileName,
          new TextureFile(
            -1,
            auxMeaning,
            auxFileName,
            auxMeaning != TextureFileType.NOT_SET
              ? ""
              : "Please indicate the file meaning",
          ),
        );
      }
      setFormErrors(errorsCopy);
      setTextureFilesDetails(auxTextureFileDetails);
    } else if (files) {
      display = "ERROR: Upload file type error! Please contact developer.";
    } else {
      return;
    }

    const formDataCopy = JSON.parse(JSON.stringify(formDataTexture));
    formDataCopy[keyForDisplay] = display === "" ? undefined : display;
    formDataCopy[keyForData] = -1;
    setFormDataTexture(formDataCopy as Texture);
    // process the GLTF file
    if (fieldId === "gltf_file" && files && files.length > 0) {
      const content = await new Response(files[0]).json();
      // @ts-ignore
      const imagesList = content.images.map(
        (info: { uri: any; name: any }) =>
          new ExpectedFileForUpload(info.uri ?? info.name),
      );
      setGltfImages(imagesList);
    } else if (fieldId === "gltf_file") {
      setGltfImages([]);
    }
  };

  const textureFileMeaningChange = (
    fileName: string,
    newMeaning: TextureFileType,
  ) => {
    // create a copy for the entire meaning map
    const dataCopy = new Map<string, TextureFile>(
      JSON.parse(JSON.stringify(Array.from(textureFilesDetails))),
    );
    // get the existing info
    const existing =
      (dataCopy.get(fileName) as TextureFile) ??
      new TextureFile(-1, newMeaning, fileName);

    // update info in copy
    dataCopy.set(
      fileName,
      new TextureFile(
        existing.id,
        newMeaning,
        existing.name,
        newMeaning != TextureFileType.NOT_SET
          ? ""
          : "Please indicate the file meaning",
      ),
    );

    setTextureFilesDetails(dataCopy);
  };

  const onFormCancel = () => {
    navigate(
      SETTINGS.go_back({
        section: AppSections.E3DS,
        id: formData.id_e3dmodel,
        subsection: AppSubSections.VIEW,
      }),
    );
  };

  const processAPIErrors = (serverError: IApiClientError) => {
    try {
      // convert the error to ChemiNovaAPI custom errors
      const apiErrors = serverError.details.response.data as APIError[];
      const formErrorsCopy: any = defaultFormErrors;
      apiErrors.forEach((apiError) => {
        formErrorsCopy[apiError.loc[0]] = apiError.msg;
      });
      setFormErrors(formErrorsCopy);
      setErrorMessage(
        "Some of the data input is invalid. Please check and try again.",
      );
    } catch (exception) {
      console.log(exception);
      setErrorMessage("Something went wrong. Please try again later!");
    }
  };

  const detailLevelOptions = Object.entries(_3DModelDetailLevel).map(
    ([key, value]) => ({
      label: key.replace(/_/g, " "),
      value: value.toString(),
    }),
  );
  detailLevelOptions.splice(0, 0, {
    value: "",
    label: "-- please select detail level --",
  });

  const licenceTypeOptions = Object.entries(_3DModelLicenseType).map(
    ([key, value]) => ({
      label: key.replace(/_/g, " "),
      value: value.toString(),
    }),
  );
  licenceTypeOptions.splice(0, 0, {
    value: "",
    label: "-- please select licence type --",
  });

  const textureTypeOptions = Object.entries(TextureType).map(
    ([key, value]) => ({
      label: key.replace(/_/g, " "),
      value: value.toString(),
    }),
  );
  textureTypeOptions.splice(0, 0, {
    value: "",
    label: "-- please select texture type --",
  });

  const pageTitle = id ? "Edit 3D Scan" : "Add 3D Scan";
  const confirmLabel = id ? "Update 3D Scan" : "Add 3D Scan";

  const deleteButton = id ? (
    <Button
      label="Delete 3D Scan"
      purpose={ButtonAction.DELETE}
      onClick={() => {
        alertsManager.showDialogWindow({
          type: DialogWindowType.CONFIRM,
          message: "Are you sure you want to delete this record?",
          cancelLabel: "Cancel",
          confirmLabel: "Yes, delete record",
          onConfirm: handleDelete,
        });
      }}
    />
  ) : null;

  if (loading || uploading) {
    return (
      <Loading
        uploading={uploading}
        uploadingMessage="Please wait. Uploading files..."
        uploadingProgress={uploadProgress}
      />
    );
  }

  return (
    <FormContainer
      title={pageTitle}
      onSubmit={(e) => {
        handleSubmit(e);
      }}
      headButton={deleteButton}
    >
      <FormInputImage
        label="Picture"
        id="icon"
        placeholderImage=""
        value={ImgUtil.display_icon(formData.icon_path)}
        required={false}
        onChange={formIconChange}
      />

      <FormInputText
        label="Name"
        id="name"
        value={formData.name}
        required={true}
        onChange={formInputChange}
        error={formErrors.name}
        hint="Please provide a human-friendly identifier relevant for this scan. For example 'Low-poly 3D mesh from RTI'."
      />

      <FormTextArea
        label="Description"
        id="description"
        value={formData.description}
        required={true}
        onChange={formInputChange}
        error={formErrors.description}
        hint="Describe succintly the main points of this scan (purpose, details,...), so you can easilly differentiate between scans added to the same assessment."
      />

      <FormTextArea
        label="Sensors configuration"
        id="sensor_configuration"
        value={formData.sensor_configuration}
        required={false}
        onChange={formInputChange}
        hint="Give some details on the sensors used to scan the asset. For example: 12mp camera, on iPhone 12 pro"
      />

      <FormDropDown
        label="3D detail level"
        id="detail_level"
        value={formData.detail_level}
        required={true}
        options={detailLevelOptions}
        onChange={formInputChange}
        error={formErrors.detail_level}
      />

      <FormDropDown
        label="License type"
        id="license_type"
        value={formData.license_type}
        required={true}
        options={licenceTypeOptions}
        onChange={formInputChange}
        error={formErrors.license_type}
      />

      {!id && (
        <FormInputFile
          label="GLTF file"
          id="gltf_file"
          keyForData="id_gltf_file"
          keyForDisplay="gltf_name"
          value={formDataTexture.gltf_name}
          required={true}
          disabled={!!id}
          acceptedFileTypes=".gltf"
          onChange={formFileChangeTexture}
          error={formErrors.id_gltf_file}
        />
      )}

      {id && (
        <FormDownloadFile
          label="GLTF file"
          file_name={formDataTexture.gltf_name}
          file_upload_id={formDataTexture.id_gltf_file!}
          warning_message="You cannot change this entry. Updated versions should be published as
        new entries."
        />
      )}

      {!id && (
        <FormInputFile
          label="Binary file"
          id="binary_file"
          keyForData="id_file"
          keyForDisplay="bin_name"
          value={formData.bin_name}
          required={true}
          disabled={!!id}
          acceptedFileTypes=".bin"
          onChange={formFileChange}
          uploadDetails=" | Uploaded file must be compatible with GLTF format"
          error={formErrors.binary_file}
        />
      )}

      {id && (
        <FormDownloadFile
          label="Binary file"
          file_name={formData.bin_name}
          file_upload_id={formData.id_file}
          warning_message="You cannot change this entry. Updated versions should be published as
        new entries."
        />
      )}

      <div>
        <label className="form-label">
          Default texture information*
          {id && (
            <Button
              purpose={ButtonAction.EDIT}
              type={ButtonType.ICON_TEXT}
              label="Edit"
              onClick={() => {
                navigate(
                  SETTINGS.get_path({
                    section: AppSections.TEXTURES,
                    id: formDataTexture.id,
                    subsection: AppSubSections.EDIT,
                  }),
                );
              }}
            />
          )}
        </label>
        <div className="form-input-group-vertical">
          <FormInputText
            label="Name"
            id="name"
            value={formDataTexture.name}
            required={true}
            placeholder="Name of texture"
            onChange={formInputChangeTexture}
            error={formErrors.nameTexture}
            hint="Please provide a human-friendly identifier relevant for this texture. For example 'Created with RGB drone scan'."
            disabled={id !== undefined}
          />

          <FormTextArea
            label="Description"
            id="description"
            value={formDataTexture.description}
            required={true}
            onChange={formInputChangeTexture}
            error={formErrors.descriptionTexture}
            hint="Describe succintly the main points of this texture (source sensor, creation methodology, ...), so you can easilly differentiate between multiple textures added to the same scan."
            disabled={id !== undefined}
          />

          <FormDropDown
            label="Texture type"
            id="type"
            value={formDataTexture.type}
            required={true}
            options={textureTypeOptions}
            onChange={formInputChangeTexture}
            error={formErrors.type}
            disabled={id !== undefined}
          />

          {!id && (
            <>
              <FormInputFile
                label="Image files"
                id="textures"
                keyForData="textures"
                keyForDisplay="textures_name"
                value={formDataTexture.textures_name}
                required={false}
                disabled={!!id}
                multipleFiles={true}
                acceptedFileTypes=".png, .jpg, .tiff"
                expectedFilesList={gltfImages}
                uploadDetails=". All files must be selected in the same browse action"
                onChange={formFileChangeTexture}
                error={formErrors.gltf_images}
              />

              {uploadManagerTexture.getFiles("textures").length > 0 &&
                uploadManagerTexture.getFiles("textures").map((texture) => {
                  var fileDetailsRef = textureFilesDetails.get(
                    texture.file.name,
                  );
                  return (
                    <FormUploadTextureFile
                      key={texture.uniqueID}
                      identifier={texture.file.name}
                      label={texture.file.name}
                      type={fileDetailsRef?.texture_type}
                      onChange={textureFileMeaningChange}
                      warning_message={fileDetailsRef?.error}
                    />
                  );
                })}
            </>
          )}

          {id &&
            Array.isArray(formDataTexture.textures) &&
            formDataTexture.textures.length > 0 && (
              <label className="form-label">Image files:</label>
            )}

          {id &&
            Array.isArray(formDataTexture.textures) &&
            formDataTexture.textures.map((texture) => {
              return (
                <FormDownloadFile
                  key={texture.id}
                  file_name={texture.name}
                  file_upload_id={texture.id}
                />
              );
            })}
        </div>
      </div>

      {errorMessage && (
        <div className="form-submit-error">
          <FaInfoCircle />
          <div>{errorMessage}</div>
        </div>
      )}

      {/* License Agreement group */}
      <div className="form-input-group mt-4 flex flex-col gap-2 pl-4">
        <label className="text-sm">{EXPLANATION_HEADER}</label>

        <label className="flex items-center gap-2 text-sm mt-1">
          <input
            type="checkbox"
            checked={agreeTerms}
            onChange={(e) => setAgreeTerms(e.target.checked)}
          />
          <span>
            I agree with the{" "}
            <a
              href="#"
              onClick={(e) => {
                e.preventDefault();
                alertsManager.showDialogWindow({
                  type: DialogWindowType.LICENSE,
                  message: "license",
                });
              }}
              rel="noopener noreferrer"
              className="text-blue-600 underline"
            >
              Terms and Conditions
            </a>
          </span>
        </label>
      </div>

      <div className="form-buttons-container">
        <Button
          label="Cancel"
          purpose={ButtonAction.CANCEL}
          onClick={() => onFormCancel()}
          className="w-full"
        />
        <Button
          label={confirmLabel}
          purpose={ButtonAction.SAVE}
          isSubmit={true}
          disabled={!agreeTerms}
          className="w-full"
        />
      </div>
    </FormContainer>
  );
}

function validateSiteForm(
  formData: _3DInstance,
  formDataTexture: Texture,
  texturesForUpload: string[],
  gltfTextures: ExpectedFileForUpload[],
  textureFilesDetails: Map<string, TextureFile>,
): I3dInstanceFormErrors {
  let errors: I3dInstanceFormErrors = {
    name: "",
    description: "",
    nameTexture: "",
    descriptionTexture: "",
    detail_level: "",
    license_type: "",
    binary_file: "",
    id_gltf_file: "",
    type: "",
    gltf_images: "",
  };

  errors.name =
    formData.name.length < 4 ? "Name should be at least 4 characters long" : "";
  errors.description =
    formData.description.length < 10
      ? "Description should be at least 10 characters long"
      : "";
  errors.nameTexture =
    formDataTexture.name.length < 4
      ? "Name should be at least 4 characters long"
      : "";
  errors.descriptionTexture =
    formDataTexture.description.length < 10
      ? "Description should be at least 10 characters long"
      : "";
  errors.detail_level = !formData.detail_level
    ? "Please select the detail level of this 3D instance"
    : "";
  errors.license_type = !formData.license_type
    ? "Please select the license type for this 3D instance"
    : "";
  errors.binary_file = !formData.bin_name
    ? "Please select the 3D model that will be assigned to this record"
    : "";
  errors.type = !formDataTexture.type
    ? "Please select the type of the default texture"
    : "";
  errors.id_gltf_file = !formDataTexture.gltf_name
    ? "Please select the GLTF file that describes this scan"
    : "";

  // get the list of textures prepared for upload
  texturesForUpload.sort((a, b) => (a < b ? -1 : a > b ? 1 : 0));
  gltfTextures.sort((a, b) => (a.name < b.name ? -1 : a.name > b.name ? 1 : 0));
  let texturesError = texturesForUpload.length !== gltfTextures.length;
  if (!texturesError) {
    for (let i = 0; i < texturesForUpload.length; i++) {
      if (texturesForUpload[i] !== gltfTextures[i].name) {
        texturesError = true;
        break;
      }
    }
  }
  errors.gltf_images = texturesError
    ? "Selected textured do not match the ones referenced in the GLTF file."
    : "";

  if (errors.gltf_images === "") {
    // check if all the files have the meaning saved
    var withNoMeaning = false;
    textureFilesDetails.forEach((item) => {
      if (item.error != "") withNoMeaning = true;
    });
    errors.gltf_images = withNoMeaning
      ? "Please indicate the meaning for all the selected files."
      : "";
  }

  return errors;
}

function isFormInvalid(errors: I3dInstanceFormErrors): boolean {
  return (
    errors.name !== "" ||
    errors.description !== "" ||
    errors.nameTexture !== "" ||
    errors.descriptionTexture !== "" ||
    errors.detail_level !== "" ||
    errors.license_type !== "" ||
    errors.binary_file !== "" ||
    errors.id_gltf_file !== "" ||
    errors.type !== "" ||
    errors.gltf_images !== ""
  );
}

export default _3dInstanceAddEdit;
